import { Form as FormikForm, useFormikContext } from 'formik';
import type { FC, ReactNode } from 'react';
import { useEffect } from 'react';
import { FieldRegistryProvider } from 'src/components/form/field-registry';
import type { FormModeContextValue } from 'src/components/form/form-mode-context';
import { FormModeProvider } from 'src/components/form/form-mode-context';
import { usePrevious } from 'src/hooks/use-previous';
import { cn } from 'src/utils/cn';

export const formGapClasses = 'gap-xl legacy:gap-[1.5rem]';

export const SimpleFormContainer: FC<Props> = ({
  children,
  mode,
  'data-testid': testid,
}) => {
  const { status, setStatus, values } = useFormikContext();
  const prevValues = usePrevious(values);

  // reset global server error on *any* change
  useEffect(() => {
    if (status === true && values !== prevValues) {
      setStatus(undefined);
    }
  }, [status, values, prevValues, setStatus]);

  return (
    <FormModeProvider mode={mode}>
      <FormikForm noValidate data-testid={testid}>
        <FieldRegistryProvider>{children}</FieldRegistryProvider>
      </FormikForm>
    </FormModeProvider>
  );
};

type Props = {
  children: ReactNode;
  /**
   * Form fields might behave slightly differently depending on the `mode` of our form.
   * Currently we have three cases:
   * - regular: Your regular form usually used to submit data.
   * - filter: A form which is used for filtering data.
   * - search: A form which is used for searching data.
   */
  mode: FormModeContextValue;
  'data-testid'?: string;
};

export const FormContainer: FC<Props> = ({
  children,
  mode,
  'data-testid': testid,
}) => {
  return (
    <SimpleFormContainer mode={mode} data-testid={testid}>
      <div className={cn('grid items-center', formGapClasses)}>{children}</div>
    </SimpleFormContainer>
  );
};
